/*
 * Solitaire — the rules of Klondike, Spider and FreeCell with no DOM.
 *
 *   Solitaire.deal(game, number, variant)  the deal IS its number
 *   Solitaire.legal / apply / undo         one move, taken back to the exact previous state
 *   Solitaire.session(...)                 a game in progress: history, redo, counters, save / restore
 *
 * A classic script on purpose: the page loads it with a tag, the solver worker
 * with importScripts, node tests through vm. GameKit must be loaded first.
 * Why each rule is shaped the way it is — docs/why/solitaire.md.
 *
 * A card is one integer:  uid << 7 | up << 6 | face,  face = suit * 13 + rank,
 * rank 0 = ace … 12 = king, suit 0 ♠ 1 ♥ 2 ♦ 3 ♣ (1 and 2 are red). The uid is
 * the physical card — Spider has every face more than once, and the table has
 * to know which of the two kings of spades is flying.
 *
 * A move is {k:'m', f, t, n} (n cards from the top of pile f onto pile t),
 * {k:'d'} (turn the stock: draw in Klondike, a row in Spider) or {k:'r'}
 * (Klondike: the waste goes back to the stock).
 */
(function (root) {
    'use strict';

    var UP = 64;
    function face(c) { return c & 63; }
    function rank(c) { return (c & 63) % 13; }
    function suit(c) { return ((c & 63) / 13) | 0; }
    function isUp(c) { return (c & UP) !== 0; }
    function uid(c) { return c >> 7; }
    function isRed(c) { var s = suit(c); return s === 1 || s === 2; }
    function mk(id, f, up) { return (id << 7) | (up ? UP : 0) | f; }
    function top(p) { return p.length ? p[p.length - 1] : -1; }

    function kit() {
        if (!root.GameKit) throw new Error('Solitaire needs GameKit loaded first');
        return root.GameKit;
    }
    // Seed text carries a version: a change to dealing gets a new tag instead
    // of silently re-dealing every number people have already shared.
    function seedFor(game, number) { return kit().hash('sol1|' + game + '|' + number); }

    var MAX_NUMBER = 999999999;
    function cleanNumber(game, n) {
        n = Math.floor(+n);
        var max = game === 'freecell' ? 2147483647 : MAX_NUMBER;
        if (!(n >= 1) || n > max) return 0;
        return n;
    }

    // ══ Klondike ═════════════════════════════════════════════════════════════
    // piles: 0 stock · 1 waste · 2–5 foundations (2 + suit) · 6–12 tableau
    var K_STOCK = 0, K_WASTE = 1, K_FOUND = 2, K_TAB = 6;

    var klondike = {
        id: 'klondike',
        piles: 13,
        cols: 7,
        kinds: ['stock', 'waste', 'found', 'found', 'found', 'found', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab'],
        tab: K_TAB,
        found: K_FOUND,

        variant: function (v) {
            v = v || {};
            var draw = +v.draw === 3 ? 3 : 1;
            var vegas = v.score === 'vegas';
            var passes = +v.passes === 1 ? 1 : (+v.passes === 3 ? 3 : 0);
            return { draw: draw, passes: passes, score: vegas ? 'vegas' : 'std' };
        },
        variantKey: function (v) {
            return 'k' + v.draw + (v.passes ? 'p' + v.passes : '') + (v.score === 'vegas' ? 'v' : '');
        },

        deal: function (number, v) {
            var deck = [], i;
            for (i = 0; i < 52; i++) deck.push(i);
            kit().rng(seedFor('klondike', number)).shuffle(deck);
            var P = [];
            for (i = 0; i < 13; i++) P.push([]);
            var p = 0;
            // Row by row, the way a hand deals it: one card to each remaining column.
            for (var row = 0; row < 7; row++) {
                for (var col = row; col < 7; col++) {
                    var f = deck[p++];
                    P[K_TAB + col].push(mk(f, f, col === row));
                }
            }
            // The stock is drawn from its end: deck[28] is the first card turned.
            for (i = 51; i >= 28; i--) P[K_STOCK].push(mk(deck[i], deck[i], false));
            return { g: 'klondike', v: v, n: number, piles: P, score: v.score === 'vegas' ? -52 : 0, moves: 0, rec: 0 };
        },

        // How many cards from the top of a pile may be lifted together.
        runLength: function (st, p) {
            var pile = st.piles[p];
            if (!pile.length) return 0;
            if (p === K_STOCK) return 0;
            if (p < K_TAB) return 1;
            var n = 0;
            for (var i = pile.length - 1; i >= 0 && isUp(pile[i]); i--) n++;
            return n;
        },

        canDrop: function (st, f, n, t) {
            if (f === t || n < 1) return false;
            var P = st.piles, src = P[f];
            if (f === K_STOCK || t === K_STOCK || t === K_WASTE) return false;
            if (n > this.runLength(st, f)) return false;
            var c = src[src.length - n];
            if (t < K_TAB) {
                return n === 1 && t === K_FOUND + suit(c) && rank(c) === P[t].length;
            }
            var dst = P[t];
            if (!dst.length) return rank(c) === 12;
            var d = top(dst);
            return isUp(d) && isRed(d) !== isRed(c) && rank(d) === rank(c) + 1;
        },

        canRecycle: function (st) {
            if (st.piles[K_STOCK].length || !st.piles[K_WASTE].length) return false;
            return !st.v.passes || st.rec < st.v.passes - 1;
        },

        legal: function (st, mv) {
            if (mv.k === 'd') return st.piles[K_STOCK].length > 0;
            if (mv.k === 'r') return this.canRecycle(st);
            return mv.k === 'm' && mv.f >= 0 && mv.f < 13 && mv.t >= 0 && mv.t < 13 && this.canDrop(st, mv.f, mv.n, mv.t);
        },

        apply: function (st, mv) {
            var P = st.piles, i, ds = 0, vegas = st.v.score === 'vegas';
            var rec = { k: mv.k, f: mv.f | 0, t: mv.t | 0, n: mv.n | 0, fl: 0, ds: 0 };
            if (mv.k === 'd') {
                rec.f = K_STOCK; rec.t = K_WASTE;
                rec.n = Math.min(st.v.draw, P[K_STOCK].length);
                for (i = 0; i < rec.n; i++) P[K_WASTE].push(P[K_STOCK].pop() | UP);
            } else if (mv.k === 'r') {
                rec.f = K_WASTE; rec.t = K_STOCK;
                rec.n = P[K_WASTE].length;
                while (P[K_WASTE].length) P[K_STOCK].push(P[K_WASTE].pop() & ~UP);
                st.rec++;
                // Windows scoring: every new pass costs 100 when turning one
                // card, 20 from the fourth pass on when turning three.
                if (!vegas) ds = st.v.draw === 1 ? -100 : (st.rec >= 3 ? -20 : 0);
            } else {
                var src = P[mv.f], moved = src.splice(src.length - mv.n, mv.n);
                for (i = 0; i < moved.length; i++) P[mv.t].push(moved[i]);
                if (mv.f >= K_TAB && src.length && !isUp(top(src))) {
                    src[src.length - 1] |= UP;
                    rec.fl = 1;
                    if (!vegas) ds += 5;
                }
                var toF = mv.t < K_TAB, fromF = mv.f >= K_FOUND && mv.f < K_TAB;
                if (vegas) ds += toF ? 5 : (fromF ? -5 : 0);
                else if (toF) ds += 10;
                else if (fromF) ds -= 15;
                else if (mv.f === K_WASTE) ds += 5;
            }
            if (!vegas && st.score + ds < 0) ds = -st.score;
            rec.ds = ds;
            st.score += ds;
            st.moves++;
            return rec;
        },

        undo: function (st, rec) {
            var P = st.piles, i;
            st.moves--;
            st.score -= rec.ds;
            if (rec.k === 'd') {
                for (i = 0; i < rec.n; i++) P[K_STOCK].push(P[K_WASTE].pop() & ~UP);
            } else if (rec.k === 'r') {
                st.rec--;
                while (P[K_STOCK].length) P[K_WASTE].push(P[K_STOCK].pop() | UP);
            } else {
                var src = P[rec.f], dst = P[rec.t];
                if (rec.fl) src[src.length - 1] &= ~UP;
                var moved = dst.splice(dst.length - rec.n, rec.n);
                for (i = 0; i < moved.length; i++) src.push(moved[i]);
            }
        },

        won: function (st) {
            var P = st.piles;
            return P[2].length + P[3].length + P[4].length + P[5].length === 52;
        },

        // A foundation move that can never be regretted: nothing left in play
        // could still want this card as a place to stand. Aces and twos always
        // qualify; a higher card does once both opposite-colour foundations are
        // within one rank of it (or within two, with its colour-mate within three).
        safeUp: function (st, c) {
            var r = rank(c), s = suit(c), P = st.piles;
            if (r < 2) return true;
            var o1 = isRed(c) ? 0 : 1, o2 = isRed(c) ? 3 : 2, mate = 3 - s;
            var lo = Math.min(P[K_FOUND + o1].length, P[K_FOUND + o2].length);
            return lo >= r || (lo >= r - 1 && P[K_FOUND + mate].length >= r - 2);
        },

        // The next card the table may send up on its own, or null. The waste
        // joins in only when one card is turned at a time: with three, taking
        // the top card changes which cards the next pass can reach.
        autoMove: function (st, mode) {
            var P = st.piles, from = [];
            for (var i = 0; i < 7; i++) from.push(K_TAB + i);
            if (st.v.draw === 1) from.push(K_WASTE);
            for (var j = 0; j < from.length; j++) {
                var c = top(P[from[j]]);
                if (c < 0 || !isUp(c)) continue;
                var t = K_FOUND + suit(c);
                if (rank(c) !== P[t].length) continue;
                if (mode === 'all' || this.safeUp(st, c)) return { k: 'm', f: from[j], t: t, n: 1 };
            }
            return null;
        },

        // Where a tapped run goes, best first.
        tapTargets: function (st, f, n) {
            var out = [], P = st.piles, i, t;
            if (f === K_STOCK || (f >= K_FOUND && f < K_TAB)) return out;
            var src = P[f];
            if (n < 1 || n > src.length) return out;
            var c = src[src.length - n];
            if (n === 1 && this.canDrop(st, f, 1, K_FOUND + suit(c))) out.push(K_FOUND + suit(c));
            var filled = [], empty = [];
            for (i = 0; i < 7; i++) {
                t = K_TAB + i;
                if (t === f || !this.canDrop(st, f, n, t)) continue;
                if (P[t].length) filled.push(t); else empty.push(t);
            }
            // The column with more hidden cards under it is the one worth feeding.
            filled.sort(function (a, b) { return down(P[b]) - down(P[a]) || a - b; });
            out = out.concat(filled);
            // A king that already stands on an empty column gains nothing by moving to another.
            var standsOnBase = f >= K_TAB && n === src.length;
            if (!standsOnBase && empty.length) out.push(empty[0]);
            return out;
        },

        stockAction: function (st) {
            if (st.piles[K_STOCK].length) return { mv: { k: 'd' } };
            if (!st.piles[K_WASTE].length) return { why: 'empty' };
            if (this.canRecycle(st)) return { mv: { k: 'r' } };
            return { why: 'passes' };
        },

        // Every legal move with a rough worth: the fallback hint when the
        // solver has no answer, and the test of "is there anything left to do".
        ranked: function (st) {
            var out = [], P = st.piles, i, j, n, t, c;
            var sources = [K_WASTE];
            for (i = 0; i < 7; i++) sources.push(K_TAB + i);
            for (j = 0; j < sources.length; j++) {
                var f = sources[j], src = P[f], run = this.runLength(st, f);
                for (n = 1; n <= run; n++) {
                    c = src[src.length - n];
                    var hidden = f >= K_TAB && n === run && src.length > run;
                    var whole = f >= K_TAB && n === src.length;
                    if (n === 1 && this.canDrop(st, f, 1, K_FOUND + suit(c))) {
                        out.push({ mv: { k: 'm', f: f, t: K_FOUND + suit(c), n: 1 }, w: f === K_WASTE ? 85 : 90 + (hidden ? 5 : 0) });
                    }
                    var gaveEmpty = false;
                    for (i = 0; i < 7; i++) {
                        t = K_TAB + i;
                        if (t === f || !this.canDrop(st, f, n, t)) continue;
                        if (!P[t].length) {
                            if (whole || gaveEmpty) continue;
                            gaveEmpty = true;
                        }
                        var w = f === K_WASTE ? 60 : (hidden ? 80 : (whole ? 45 : 10));
                        out.push({ mv: { k: 'm', f: f, t: t, n: n }, w: w });
                    }
                }
            }
            for (i = 0; i < 4; i++) {
                f = K_FOUND + i;
                if (!P[f].length) continue;
                for (j = 0; j < 7; j++) {
                    if (this.canDrop(st, f, 1, K_TAB + j)) out.push({ mv: { k: 'm', f: f, t: K_TAB + j, n: 1 }, w: 1 });
                }
            }
            if (P[K_STOCK].length) out.push({ mv: { k: 'd' }, w: 30 });
            else if (this.canRecycle(st)) out.push({ mv: { k: 'r' }, w: 20 });
            out.sort(function (a, b) { return b.w - a.w; });
            return out;
        },

        // Nothing but turning the stock is left, and no card in it has a place.
        stuck: function (st) {
            var list = this.ranked(st), P = st.piles, i, j;
            for (i = 0; i < list.length; i++) if (list[i].w >= 40) return false;
            var talon = P[K_STOCK].concat(P[K_WASTE]);
            if (!talon.length) return true;
            if (!P[K_STOCK].length && !this.canRecycle(st)) return true;
            for (i = 0; i < talon.length; i++) {
                var c = talon[i];
                if (rank(c) === P[K_FOUND + suit(c)].length) return false;
                for (j = 0; j < 7; j++) {
                    var dst = P[K_TAB + j];
                    if (!dst.length) { if (rank(c) === 12) return false; continue; }
                    var d = top(dst);
                    if (isRed(d) !== isRed(c) && rank(d) === rank(c) + 1) return false;
                }
            }
            return true;
        },

        // The rest of the game plays itself: every card is face up and each
        // one in the stock can be reached. Returns the moves, or null.
        finishLine: function (st) {
            var P = st.piles, i;
            for (i = 0; i < 7; i++) if (down(P[K_TAB + i])) return null;
            var talon = P[K_STOCK].length + P[K_WASTE].length;
            if (talon && (st.v.draw !== 1 || st.v.passes)) return null;
            var sim = clone(st), out = [], guard = 0;
            while (!this.won(sim) && guard++ < 600) {
                var mv = this.autoMove(sim, 'all');
                if (!mv) {
                    var a = this.stockAction(sim);
                    if (!a.mv) return null;
                    mv = a.mv;
                }
                this.apply(sim, mv);
                out.push(mv);
            }
            return this.won(sim) ? out : null;
        }
    };

    function down(pile) {
        var n = 0;
        while (n < pile.length && !isUp(pile[n])) n++;
        return n;
    }

    // ══ FreeCell ═════════════════════════════════════════════════════════════
    // piles: 0–3 cells · 4–7 foundations (4 + suit) · 8–15 tableau
    var F_CELL = 0, F_FOUND = 4, F_TAB = 8;
    // Microsoft numbers its suits clubs, diamonds, hearts, spades.
    var MS_SUIT = [3, 2, 1, 0];

    // The deals of Microsoft FreeCell: game #617 here is the game #617 people
    // have played since 1995. Its generator is the C runtime's rand(), not
    // GameKit's — the numbers are a shared language, and a better generator
    // would speak a different one.
    function msDeal(number) {
        var deck = [], i, seed = number, cols = [[], [], [], [], [], [], [], []];
        for (i = 0; i < 52; i++) deck.push(i);
        for (i = 0; i < 52; i++) {
            var left = 52 - i;
            seed = (seed * 214013 + 2531011) % 2147483648;
            var j = Math.floor(seed / 65536) % left;
            var c = deck[j];
            deck[j] = deck[left - 1];
            cols[i % 8].push(MS_SUIT[c % 4] * 13 + ((c / 4) | 0));
        }
        return cols;
    }

    var freecell = {
        id: 'freecell',
        piles: 16,
        cols: 8,
        kinds: ['cell', 'cell', 'cell', 'cell', 'found', 'found', 'found', 'found', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab'],
        tab: F_TAB,
        found: F_FOUND,

        variant: function () { return {}; },
        variantKey: function () { return 'fc'; },

        deal: function (number, v) {
            var cols = msDeal(number), P = [], i, j;
            for (i = 0; i < 16; i++) P.push([]);
            for (i = 0; i < 8; i++) for (j = 0; j < cols[i].length; j++) P[F_TAB + i].push(mk(cols[i][j], cols[i][j], true));
            return { g: 'freecell', v: v, n: number, piles: P, score: 0, moves: 0 };
        },

        runLength: function (st, p) {
            var pile = st.piles[p];
            if (!pile.length) return 0;
            if (p < F_FOUND) return 1;
            if (p < F_TAB) return 0;
            var n = 1;
            for (var i = pile.length - 1; i > 0; i--) {
                var a = pile[i - 1], b = pile[i];
                if (isRed(a) === isRed(b) || rank(a) !== rank(b) + 1) break;
                n++;
            }
            return n;
        },

        // How many cards one move may carry: each free cell holds one on the
        // way, and every empty column doubles the lot. A column being moved
        // into does not count as empty.
        capacity: function (st, t) {
            var P = st.piles, cells = 0, cols = 0, i;
            for (i = 0; i < 4; i++) if (!P[F_CELL + i].length) cells++;
            for (i = 0; i < 8; i++) if (!P[F_TAB + i].length && F_TAB + i !== t) cols++;
            return (cells + 1) * Math.pow(2, cols);
        },

        canDrop: function (st, f, n, t) {
            if (f === t || n < 1) return false;
            var P = st.piles, src = P[f];
            if (f >= F_FOUND && f < F_TAB) return false;
            if (n > this.runLength(st, f)) return false;
            var c = src[src.length - n];
            if (t < F_FOUND) return n === 1 && !P[t].length;
            if (t < F_TAB) return n === 1 && t === F_FOUND + suit(c) && rank(c) === P[t].length;
            if (n > this.capacity(st, t)) return false;
            var dst = P[t];
            if (!dst.length) return true;
            var d = top(dst);
            return isRed(d) !== isRed(c) && rank(d) === rank(c) + 1;
        },

        legal: function (st, mv) {
            return mv.k === 'm' && mv.f >= 0 && mv.f < 16 && mv.t >= 0 && mv.t < 16 && this.canDrop(st, mv.f, mv.n, mv.t);
        },

        apply: function (st, mv) {
            var P = st.piles, src = P[mv.f], moved = src.splice(src.length - mv.n, mv.n);
            for (var i = 0; i < moved.length; i++) P[mv.t].push(moved[i]);
            st.moves++;
            return { k: 'm', f: mv.f, t: mv.t, n: mv.n, fl: 0, ds: 0 };
        },

        undo: function (st, rec) {
            var P = st.piles, dst = P[rec.t], moved = dst.splice(dst.length - rec.n, rec.n);
            for (var i = 0; i < moved.length; i++) P[rec.f].push(moved[i]);
            st.moves--;
        },

        won: function (st) {
            var P = st.piles;
            return P[4].length + P[5].length + P[6].length + P[7].length === 52;
        },

        safeUp: function (st, c) {
            var r = rank(c), s = suit(c), P = st.piles;
            if (r < 2) return true;
            var o1 = isRed(c) ? 0 : 1, o2 = isRed(c) ? 3 : 2, mate = 3 - s;
            var lo = Math.min(P[F_FOUND + o1].length, P[F_FOUND + o2].length);
            return lo >= r || (lo >= r - 1 && P[F_FOUND + mate].length >= r - 2);
        },

        autoMove: function (st, mode) {
            var P = st.piles;
            for (var p = 0; p < 16; p++) {
                if (p >= F_FOUND && p < F_TAB) continue;
                var c = top(P[p]);
                if (c < 0) continue;
                var t = F_FOUND + suit(c);
                if (rank(c) !== P[t].length) continue;
                if (mode === 'all' || this.safeUp(st, c)) return { k: 'm', f: p, t: t, n: 1 };
            }
            return null;
        },

        tapTargets: function (st, f, n) {
            var out = [], P = st.piles, i, t;
            if (f >= F_FOUND && f < F_TAB) return out;
            var src = P[f];
            if (n < 1 || n > src.length) return out;
            var c = src[src.length - n];
            if (n === 1 && this.canDrop(st, f, 1, F_FOUND + suit(c))) out.push(F_FOUND + suit(c));
            var empty = -1;
            for (i = 0; i < 8; i++) {
                t = F_TAB + i;
                if (t === f || !this.canDrop(st, f, n, t)) continue;
                if (P[t].length) out.push(t);
                else if (empty < 0) empty = t;
            }
            // A whole column carried to another empty column changes nothing.
            if (empty >= 0 && !(f >= F_TAB && n === src.length)) out.push(empty);
            if (n === 1 && f >= F_TAB) {
                for (i = 0; i < 4; i++) if (!P[F_CELL + i].length) { out.push(F_CELL + i); break; }
            }
            return out;
        },

        stockAction: function () { return { why: 'none' }; },

        ranked: function (st) {
            var out = [], P = st.piles, i, n, t, f, c;
            for (f = 0; f < 16; f++) {
                if (f >= F_FOUND && f < F_TAB) continue;
                var src = P[f], run = this.runLength(st, f);
                if (!run) continue;
                c = top(src);
                if (this.canDrop(st, f, 1, F_FOUND + suit(c))) out.push({ mv: { k: 'm', f: f, t: F_FOUND + suit(c), n: 1 }, w: 90 });
                var gaveEmpty = false;
                for (n = run; n >= 1; n--) {
                    for (i = 0; i < 8; i++) {
                        t = F_TAB + i;
                        if (t === f || !this.canDrop(st, f, n, t)) continue;
                        if (!P[t].length) {
                            if (gaveEmpty || (f >= F_TAB && n === src.length)) continue;
                            gaveEmpty = true;
                            out.push({ mv: { k: 'm', f: f, t: t, n: n }, w: f < F_FOUND ? 15 : 30 });
                        } else {
                            out.push({ mv: { k: 'm', f: f, t: t, n: n }, w: (f < F_FOUND ? 70 : 60) + (n === run ? 2 : 0) });
                        }
                    }
                }
                if (f >= F_TAB) {
                    for (i = 0; i < 4; i++) {
                        if (!P[F_CELL + i].length) { out.push({ mv: { k: 'm', f: f, t: F_CELL + i, n: 1 }, w: 20 }); break; }
                    }
                }
            }
            out.sort(function (a, b) { return b.w - a.w; });
            return out;
        },

        stuck: function (st) { return !this.won(st) && this.ranked(st).length === 0; },

        finishLine: function (st) {
            var sim = clone(st), out = [], guard = 0;
            while (!this.won(sim) && guard++ < 60) {
                var mv = this.autoMove(sim, 'all');
                if (!mv) return null;
                this.apply(sim, mv);
                out.push(mv);
            }
            return this.won(sim) && out.length ? out : null;
        }
    };

    // ══ Spider ═══════════════════════════════════════════════════════════════
    // piles: 0 stock · 1–8 finished suits · 9–18 tableau
    var S_STOCK = 0, S_FOUND = 1, S_TAB = 9;

    var spider = {
        id: 'spider',
        piles: 19,
        cols: 10,
        kinds: ['stock', 'found', 'found', 'found', 'found', 'found', 'found', 'found', 'found',
            'tab', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab', 'tab'],
        tab: S_TAB,
        found: S_FOUND,

        variant: function (v) {
            v = v || {};
            var s = +v.suits;
            return { suits: s === 1 ? 1 : (s === 4 ? 4 : 2) };
        },
        variantKey: function (v) { return 's' + v.suits; },

        // One shuffle for all three difficulties: deal #N in one suit is the
        // four-suit deal #N with the colours folded together, so a deal number
        // means the same lay of ranks whatever the number of suits.
        deal: function (number, v) {
            var order = [], i;
            for (i = 0; i < 104; i++) order.push(i);
            kit().rng(seedFor('spider', number)).shuffle(order);
            var deck = [];
            for (i = 0; i < 104; i++) {
                var s4 = ((order[i] % 52) / 13) | 0, r = order[i] % 13;
                var s = v.suits === 4 ? s4 : (v.suits === 2 ? ((s4 === 1 || s4 === 2) ? 1 : 0) : 0);
                deck.push(s * 13 + r);
            }
            var P = [];
            for (i = 0; i < 19; i++) P.push([]);
            var p = 0;
            for (var row = 0; row < 6; row++) {
                for (var col = 0; col < 10; col++) {
                    if (row === 5 && col >= 4) continue;
                    P[S_TAB + col].push(mk(p, deck[p], false));
                    p++;
                }
            }
            for (i = 0; i < 10; i++) P[S_TAB + i][P[S_TAB + i].length - 1] |= UP;
            for (i = 103; i >= 54; i--) P[S_STOCK].push(mk(i, deck[i], false));
            return { g: 'spider', v: v, n: number, piles: P, score: 500, moves: 0, done: 0 };
        },

        runLength: function (st, p) {
            var pile = st.piles[p];
            if (p < S_TAB || !pile.length) return 0;
            var n = 1;
            for (var i = pile.length - 1; i > 0; i--) {
                var a = pile[i - 1], b = pile[i];
                if (!isUp(a) || suit(a) !== suit(b) || rank(a) !== rank(b) + 1) break;
                n++;
            }
            return n;
        },

        canDrop: function (st, f, n, t) {
            if (f === t || n < 1 || f < S_TAB || t < S_TAB) return false;
            if (n > this.runLength(st, f)) return false;
            var src = st.piles[f], dst = st.piles[t];
            if (!dst.length) return true;
            return rank(top(dst)) === rank(src[src.length - n]) + 1;
        },

        // A row may be dealt only onto ten occupied columns — unless there are
        // not ten cards left on the table to occupy them with.
        canDeal: function (st) {
            var P = st.piles, cards = 0, empty = 0;
            if (!P[S_STOCK].length) return false;
            for (var i = 0; i < 10; i++) {
                cards += P[S_TAB + i].length;
                if (!P[S_TAB + i].length) empty++;
            }
            return empty === 0 || cards < 10;
        },

        legal: function (st, mv) {
            if (mv.k === 'd') return this.canDeal(st);
            return mv.k === 'm' && mv.f >= 0 && mv.f < 19 && mv.t >= 0 && mv.t < 19 && this.canDrop(st, mv.f, mv.n, mv.t);
        },

        // King down to ace in one suit on top of a column leaves the table.
        collect: function (st, p, rec) {
            var pile = st.piles[p];
            if (pile.length < 13) return;
            var base = pile.length - 13, s = suit(pile[base]);
            for (var i = 0; i < 13; i++) {
                var c = pile[base + i];
                if (!isUp(c) || suit(c) !== s || rank(c) !== 12 - i) return;
            }
            var to = S_FOUND;
            while (st.piles[to].length) to++;
            var cards = pile.splice(base, 13), fl = 0;
            for (i = 0; i < 13; i++) st.piles[to].push(cards[i]);
            if (pile.length && !isUp(top(pile))) { pile[pile.length - 1] |= UP; fl = 1; }
            st.done++;
            rec.ds += 100;
            (rec.runs || (rec.runs = [])).push({ p: p, to: to, fl: fl });
        },

        apply: function (st, mv) {
            var P = st.piles, i;
            var rec = { k: mv.k, f: mv.f | 0, t: mv.t | 0, n: mv.n | 0, fl: 0, ds: -1 };
            if (mv.k === 'd') {
                rec.f = S_STOCK; rec.t = S_TAB; rec.n = Math.min(10, P[S_STOCK].length);
                for (i = 0; i < rec.n; i++) P[S_TAB + i].push(P[S_STOCK].pop() | UP);
                for (i = 0; i < 10; i++) this.collect(st, S_TAB + i, rec);
            } else {
                var src = P[mv.f], moved = src.splice(src.length - mv.n, mv.n);
                for (i = 0; i < moved.length; i++) P[mv.t].push(moved[i]);
                if (src.length && !isUp(top(src))) { src[src.length - 1] |= UP; rec.fl = 1; }
                this.collect(st, mv.t, rec);
            }
            st.score += rec.ds;
            st.moves++;
            return rec;
        },

        undo: function (st, rec) {
            var P = st.piles, i;
            st.moves--;
            st.score -= rec.ds;
            if (rec.runs) {
                for (i = rec.runs.length - 1; i >= 0; i--) {
                    var run = rec.runs[i], pile = P[run.p];
                    if (run.fl) pile[pile.length - 1] &= ~UP;
                    var cards = P[run.to].splice(0, 13);
                    for (var j = 0; j < 13; j++) pile.push(cards[j]);
                    st.done--;
                }
            }
            if (rec.k === 'd') {
                for (i = rec.n - 1; i >= 0; i--) P[S_STOCK].push(P[S_TAB + i].pop() & ~UP);
            } else {
                var src = P[rec.f], dst = P[rec.t];
                if (rec.fl) src[src.length - 1] &= ~UP;
                var moved = dst.splice(dst.length - rec.n, rec.n);
                for (i = 0; i < moved.length; i++) src.push(moved[i]);
            }
        },

        won: function (st) { return st.done === 8; },
        autoMove: function () { return null; },
        finishLine: function () { return null; },

        tapTargets: function (st, f, n) {
            var P = st.piles, out = [], i, t;
            if (f < S_TAB) return out;
            var src = P[f];
            if (n < 1 || n > this.runLength(st, f)) return out;
            var c = src[src.length - n], same = [], other = [], empty = -1;
            for (i = 0; i < 10; i++) {
                t = S_TAB + i;
                if (t === f || !this.canDrop(st, f, n, t)) continue;
                if (!P[t].length) { if (empty < 0) empty = t; continue; }
                if (suit(top(P[t])) === suit(c)) same.push(t); else other.push(t);
            }
            var self = this;
            // The longer the run the card joins, the closer a finished suit.
            same.sort(function (a, b) { return self.runLength(st, b) - self.runLength(st, a) || a - b; });
            out = same.concat(other);
            if (empty >= 0 && n !== src.length) out.push(empty);
            return out;
        },

        stockAction: function (st) {
            if (!st.piles[S_STOCK].length) return { why: 'empty' };
            if (!this.canDeal(st)) return { why: 'gaps' };
            return { mv: { k: 'd' } };
        },

        ranked: function (st) {
            var out = [], P = st.piles, i, n, f, t;
            for (f = S_TAB; f < S_TAB + 10; f++) {
                var src = P[f], run = this.runLength(st, f);
                for (n = run; n >= 1; n--) {
                    var c = src[src.length - n];
                    var whole = n === src.length, hidden = n === run && src.length > run && !isUp(src[src.length - run - 1]);
                    var gaveEmpty = false;
                    for (i = 0; i < 10; i++) {
                        t = S_TAB + i;
                        if (t === f || !this.canDrop(st, f, n, t)) continue;
                        var dst = P[t], w;
                        if (!dst.length) {
                            if (whole || gaveEmpty) continue;
                            gaveEmpty = true;
                            w = n === run ? 30 : 12;
                        } else if (suit(top(dst)) === suit(c)) {
                            var joined = this.runLength(st, t) + n;
                            // A run split only to be re-stacked in the same suit gains nothing.
                            if (n < run && joined <= run) w = 8;
                            else w = joined >= 13 ? 100 : 80 + Math.min(9, joined) + (hidden ? 5 : 0);
                        } else {
                            w = n < run ? 15 : (hidden ? 70 : (whole ? 55 : 40));
                        }
                        out.push({ mv: { k: 'm', f: f, t: t, n: n }, w: w });
                    }
                }
            }
            if (this.canDeal(st)) out.push({ mv: { k: 'd' }, w: 25 });
            out.sort(function (a, b) { return b.w - a.w; });
            return out;
        },

        stuck: function (st) { return !this.won(st) && this.ranked(st).length === 0; }
    };

    var GAMES = { klondike: klondike, spider: spider, freecell: freecell };

    // ══ Common ═══════════════════════════════════════════════════════════════
    function clone(st) {
        var out = {};
        for (var k in st) if (Object.prototype.hasOwnProperty.call(st, k)) out[k] = st[k];
        out.piles = st.piles.map(function (p) { return p.slice(); });
        return out;
    }

    // The position as text: equal keys ⇔ equal tables (which twin of a
    // repeated Spider card lies where is not part of the position).
    function key(st) {
        var s = '';
        for (var i = 0; i < st.piles.length; i++) {
            var p = st.piles[i];
            for (var j = 0; j < p.length; j++) s += String.fromCharCode(48 + (p[j] & 127));
            s += '|';
        }
        return s + (st.rec || 0);
    }

    function deal(game, number, variant) {
        var def = GAMES[game];
        if (!def) throw new Error('unknown game ' + game);
        number = cleanNumber(game, number);
        if (!number) throw new Error('bad deal number');
        return def.deal(number, def.variant(variant));
    }

    // ── What the table shows, beat by beat ───────────────────────────────────
    // A step is applied at once; the table plays it back as beats a person can
    // follow: the player's move (with the card it uncovers turning over), each
    // card the table then sends home on its own, a finished Spider suit leaving
    // its column. `before` is the position the step started from, `step` its
    // records (as play / undo / redo return them). Returns the positions
    // [before, after beat 1, …] — the last one is the position after the step —
    // and what each beat was. Only the piles are kept true; counters are not.
    function beats(game, before, step) {
        var sim = clone(before), out = [{ st: clone(sim), kind: 'start' }];
        for (var i = 0; i < step.length; i++) {
            var r = step[i], P = sim.piles, j, kind;
            if (r.k === 'm') {
                var src = P[r.f], moved = src.splice(src.length - r.n, r.n);
                for (j = 0; j < moved.length; j++) P[r.t].push(moved[j]);
                if (r.fl && src.length) src[src.length - 1] |= UP;
                kind = r.auto ? 'auto' : 'move';
            } else if (r.k === 'd') {
                if (game === 'spider') for (j = 0; j < r.n; j++) P[S_TAB + j].push(P[S_STOCK].pop() | UP);
                else for (j = 0; j < r.n; j++) P[K_WASTE].push(P[K_STOCK].pop() | UP);
                kind = game === 'spider' ? 'deal' : 'draw';
            } else {
                while (P[K_WASTE].length) P[K_STOCK].push(P[K_WASTE].pop() & ~UP);
                sim.rec = (sim.rec || 0) + 1;
                kind = 'recycle';
            }
            out.push({ st: clone(sim), kind: kind, rec: r });
            for (j = 0; r.runs && j < r.runs.length; j++) {
                var run = r.runs[j], pile = P[run.p], cards = pile.splice(pile.length - 13, 13);
                for (var q = 0; q < 13; q++) P[run.to].push(cards[q]);
                if (run.fl && pile.length) pile[pile.length - 1] |= UP;
                out.push({ st: clone(sim), kind: 'collect', rec: r, run: run });
            }
        }
        return out;
    }

    // ── Why not ──────────────────────────────────────────────────────────────
    // Why the n cards on top of pile p cannot move — anywhere, or onto pile
    // `to` when a drop there was refused. The words a person reads next to the
    // card instead of a silent shake. Returns { k, c, a, b, r, n } — k names the
    // sentence (js.why_* in the translations), c is the card in question, a / b
    // the cards it would need, r a rank it would need (any suit), n a count.
    // null — nothing to explain (the move is legal, or the press was nothing).
    function why(game, st, p, n, to) {
        var def = GAMES[game];
        if (!def || !st || !st.piles[p]) return null;
        var P = st.piles, kind = def.kinds[p], pile = P[p];
        if (kind === 'stock' || !pile.length || n < 1 || n > pile.length) return null;
        var c = pile[pile.length - n], r = rank(c);
        if (!isUp(c)) return { k: 'why_down' };
        if (kind === 'found' && game !== 'klondike') return { k: 'why_home' };
        if (kind === 'waste' && n > 1) return { k: 'why_waste_top' };
        if (n > def.runLength(st, p)) return { k: game === 'spider' ? 'why_s_mixed' : 'why_f_mixed' };
        var red = isRed(c);
        // the two cards a card of alternating colour would stand on
        function on() { return { a: mk(0, (red ? 0 : 1) * 13 + r + 1, true), b: mk(0, (red ? 3 : 2) * 13 + r + 1, true) }; }

        if (to != null && to >= 0) {
            if (to === p || def.canDrop(st, p, n, to)) return null;
            var tk = def.kinds[to], dst = P[to], d = top(dst);
            if (tk === 'stock' || tk === 'waste') return { k: 'why_t_nowhere' };
            if (tk === 'found') {
                if (game === 'spider') return { k: 'why_t_suits' };
                if (n > 1) return { k: 'why_t_found_one' };
                var home = P[def.found + suit(c)];
                if (!home.length && r) return { k: 'why_t_found_ace', c: c };
                return { k: 'why_t_found', c: c, a: mk(0, suit(c) * 13 + Math.min(12, home.length), true) };
            }
            if (tk === 'cell') return { k: n > 1 ? 'why_t_cell_one' : 'why_t_cell_full' };
            if (!dst.length) {
                if (game === 'klondike') return { k: 'why_t_king' };
                if (game === 'freecell') return { k: 'why_f_cap', n: def.capacity(st, to) };
                return null;
            }
            if (game === 'freecell' && n > def.capacity(st, to)) return { k: 'why_f_cap', n: def.capacity(st, to) };
            if (rank(d) !== r + 1) {
                if (r === 12) return { k: game === 'freecell' ? 'why_t_king_f' : 'why_t_king_e' };
                if (game === 'spider') return { k: 'why_t_rank_s', c: c, r: r + 1 };
                var w = on();
                return { k: 'why_t_rank', c: c, a: w.a, b: w.b };
            }
            if (game !== 'spider' && isRed(d) === red) return { k: 'why_t_color' };
            return null;
        }

        // Nowhere at all.
        if (kind === 'tab' && n === pile.length) {
            // a whole column is not carried to another empty column — it would change nothing
            for (var e = 0; e < def.cols; e++) if (def.tab + e !== p && !P[def.tab + e].length) return { k: 'why_whole' };
        }
        if (game === 'freecell' && n > 1) {
            // a place fits the run but there is not room enough to carry it
            for (var i = 0; i < 8; i++) {
                var t = F_TAB + i, dt = P[t];
                if (t === p || !dt.length) continue;
                var dd = top(dt);
                if (isRed(dd) !== red && rank(dd) === r + 1) return { k: 'why_f_cap', n: def.capacity(st, t) };
            }
        }
        if (r === 12) return { k: game === 'freecell' ? 'why_f_king' : 'why_k_king' };
        if (game === 'spider') return { k: 'why_s_on', c: c, r: r + 1 };
        var w2 = on();
        return { k: game === 'freecell' ? (n > 1 ? 'why_f_on_run' : 'why_f_on') : 'why_k_on', c: c, a: w2.a, b: w2.b };
    }

    function encodeMove(mv) {
        if (mv.k !== 'm') return mv.k;
        return 'm' + mv.f.toString(36) + mv.t.toString(36) + mv.n.toString(36);
    }
    function decodeMove(s) {
        if (s === 'd' || s === 'r') return { k: s };
        if (typeof s !== 'string' || s.length !== 4 || s[0] !== 'm') return null;
        var f = parseInt(s[1], 36), t = parseInt(s[2], 36), n = parseInt(s[3], 36);
        if (!(f >= 0) || !(t >= 0) || !(n >= 1)) return null;
        return { k: 'm', f: f, t: t, n: n };
    }

    // ── A game in progress ───────────────────────────────────────────────────
    // History is a list of steps; a step is the player's move followed by the
    // moves the table made on its own (cards sent up). Undo takes back a whole
    // step, redo plays it again. `auto` is 'safe' | 'all' | 'off'.
    function session(game, number, variant, opts) {
        opts = opts || {};
        var def = GAMES[game];
        var st = deal(game, number, variant);
        var hist = [], redo = [];
        var meta = {
            auto: opts.auto || 'safe',
            hints: 0, undos: 0, assisted: false,
            daily: opts.daily || null,
            sure: !!opts.sure
        };

        function autoFollow(step, userMove) {
            if (meta.auto === 'off') return;
            // A card the player has just taken down from a foundation stays down.
            if (userMove.k === 'm' && def.kinds[userMove.f] === 'found') return;
            var guard = 0, mv;
            while (guard++ < 60 && (mv = def.autoMove(st, meta.auto))) {
                var rec = def.apply(st, mv);
                rec.auto = 1;
                step.push(rec);
            }
        }

        var api = {
            def: def,
            meta: meta,
            get state() { return st; },
            game: game,
            number: st.n,
            variantKey: def.variantKey(st.v),

            legal: function (mv) { return def.legal(st, mv); },
            // Plays the move and whatever the table does after it. Returns the
            // step (list of records) or null when the move is not legal.
            play: function (mv, how) {
                if (!def.legal(st, mv)) return null;
                var step = [def.apply(st, mv)];
                if (!(how && how.noAuto)) autoFollow(step, mv);
                hist.push(step);
                redo.length = 0;
                return step;
            },
            canUndo: function () { return hist.length > 0; },
            canRedo: function () { return redo.length > 0; },
            undo: function () {
                var step = hist.pop();
                if (!step) return null;
                for (var i = step.length - 1; i >= 0; i--) def.undo(st, step[i]);
                redo.push(step);
                meta.undos++;
                return step;
            },
            redo: function () {
                var step = redo.pop();
                if (!step) return null;
                var again = [];
                for (var i = 0; i < step.length; i++) {
                    var rec = def.apply(st, step[i]);
                    if (step[i].auto) rec.auto = 1;
                    again.push(rec);
                }
                hist.push(again);
                return again;
            },
            steps: function () { return hist.length; },
            won: function () { return def.won(st); },
            // Score as the player sees it. Spider charges a point for a move
            // taken back, the way the Windows game always has.
            score: function () { return game === 'spider' ? st.score - meta.undos : st.score; },

            serialize: function () {
                function pack(list) {
                    return list.map(function (step) {
                        return step.map(function (r) { return (r.auto ? '+' : '') + encodeMove(r); }).join(',');
                    });
                }
                return {
                    v: 1, g: game, o: st.v, n: st.n,
                    h: pack(hist), r: pack(redo.slice().reverse()),
                    hints: meta.hints, undos: meta.undos, a: meta.assisted ? 1 : 0,
                    auto: meta.auto, daily: meta.daily, sure: meta.sure ? 1 : 0
                };
            }
        };
        return api;
    }

    // Rebuilds a saved game by playing its moves through the rules again: a
    // save that no longer fits the rules (edited, cut short, from another
    // version) comes back as null instead of as a broken table.
    function restore(data) {
        try {
            if (!data || data.v !== 1 || !GAMES[data.g]) return null;
            var def = GAMES[data.g];
            if (!cleanNumber(data.g, data.n)) return null;
            var s = session(data.g, data.n, data.o, { auto: data.auto, daily: data.daily || null, sure: !!data.sure });
            var st = s.state;
            var lists = [data.h || [], data.r || []], undone = (data.r || []).length;
            for (var part = 0; part < 2; part++) {
                for (var i = 0; i < lists[part].length; i++) {
                    var moves = String(lists[part][i]).split(',');
                    var first = decodeMove(moves[0]);
                    if (!first || !def.legal(st, first)) return null;
                    var step = s.play(first, { noAuto: true });
                    for (var j = 1; j < moves.length; j++) {
                        var text = moves[j];
                        if (text[0] !== '+') return null;
                        var mv = decodeMove(text.slice(1));
                        if (!mv || !def.legal(st, mv)) return null;
                        var rec = def.apply(st, mv);
                        rec.auto = 1;
                        step.push(rec);
                    }
                }
            }
            for (var u = 0; u < undone; u++) s.undo();
            s.meta.hints = +data.hints || 0;
            s.meta.undos = +data.undos || 0;
            s.meta.assisted = !!data.a;
            return s;
        } catch (_) { return null; }
    }

    root.Solitaire = {
        UP: UP,
        face: face, rank: rank, suit: suit, isUp: isUp, uid: uid, isRed: isRed, mk: mk, down: down,
        games: GAMES,
        MAX_NUMBER: MAX_NUMBER,
        cleanNumber: cleanNumber,
        msDeal: msDeal,
        deal: deal,
        clone: clone,
        key: key,
        beats: beats,
        why: why,
        encodeMove: encodeMove,
        decodeMove: decodeMove,
        session: session,
        restore: restore
    };
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
